import React from "react";
import styled from "styled-components";
import { Mail, Phone, MapPin, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

/*  Background Wrapper  */
const PageWrapper = styled.div`
  min-height: 100vh;
  background-image: url("https://images.unsplash.com/photo-1521791136064-7986c2920216");
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  font-family: "Poppins", sans-serif;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 20px 10px;
  }
`;

/*  Main Card  */
const Card = styled.div`
  background: #ffffff;
  width: 100%;
  max-width: 1100px;
  border-radius: 20px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  display: flex;
  overflow: hidden;
  position: relative;
  box-sizing: border-box;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

/* ❌ Close Button */
const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
  border: none;
  border-radius: 50%;
  width: 38px;
  height: 38px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.35);
    transform: scale(1.05);
  }

  @media (min-width: 901px) {
    background: rgba(0, 0, 0, 0.05);
    color: #333333;

    &:hover {
      background: rgba(0, 0, 0, 0.1);
    }
  }
`;

/*  Left Section  */
const Left = styled.div`
  background: linear-gradient(135deg, #2b59ff, #6a82fb);
  color: white;
  padding: 50px;
  flex: 1;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 36px 20px;
  }

  @media (max-width: 480px) {
    padding: 26px 14px;
  }
`;

const LeftTitle = styled.h2`
  font-size: clamp(1.4rem, 5vw, 2rem);
  margin-bottom: 24px;
  word-break: break-word;
  padding-right: 44px;

  @media (min-width: 901px) {
    padding-right: 0;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  font-size: clamp(0.85rem, 2.5vw, 0.95rem);

  @media (max-width: 480px) {
    gap: 8px;
    margin-bottom: 16px;
  }
`;

const InfoItemTop = styled(InfoItem)`
  align-items: flex-start;
  line-height: 1.55;
`;

const AddressText = styled.div`
  flex: 1;
  min-width: 0;
  font-size: clamp(0.82rem, 2.4vw, 0.92rem);
  word-break: break-word;
`;

const IconBox = styled.div`
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  max-width: 36px;
  max-height: 36px;
  box-sizing: border-box;
  margin-top: 2px;

  @media (max-width: 360px) {
    flex: 0 0 32px;
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    max-width: 32px;
    max-height: 32px;
  }
`;

const EmailLink = styled.a`
  color: inherit;
  text-decoration: none;
  white-space: nowrap;
  font-size: clamp(0.72rem, 2.9vw, 0.95rem);
  letter-spacing: -0.2px;

  &:hover {
    text-decoration: underline;
  }
`;

const PhoneLink = styled.a`
  color: inherit;
  text-decoration: none;
  white-space: nowrap;
  font-size: clamp(0.82rem, 2.7vw, 0.95rem);

  &:hover {
    text-decoration: underline;
  }
`;

/*  Right Section  */
const Right = styled.div`
  flex: 1.2;
  padding: 50px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 36px 20px;
  }

  @media (max-width: 480px) {
    padding: 26px 14px;
  }
`;

const FormTitle = styled.h2`
  font-size: clamp(1.3rem, 4vw, 1.8rem);
  margin-bottom: 24px;
  color: #333;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
`;

const Input = styled.input`
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #ccc;
  font-size: 0.95rem;
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #2b59ff;
  }
`;

const Textarea = styled.textarea`
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #ccc;
  font-size: 0.95rem;
  resize: vertical;
  min-height: 110px;
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #2b59ff;
  }
`;

const Button = styled.button`
  background: #2b59ff;
  color: white;
  border: none;
  padding: 14px;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 10px;
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover {
    opacity: 0.92;
    transform: translateY(-1px);
  }
`;

/*  Component  */
export default function Contact() {
  const navigate = useNavigate();

  return (
    <PageWrapper>
      <Card>
        {/* ❌ CLOSE ICON */}
        <CloseBtn onClick={() => navigate(-1)} aria-label="Close">
          <X size={20} />
        </CloseBtn>

        {/* LEFT */}
        <Left>
          <LeftTitle>Contact Information</LeftTitle>

          <InfoItem>
            <IconBox>
              <Mail size={16} />
            </IconBox>
            <EmailLink href="mailto:khushboo.sharma@berrybloomer.com">
              khushboo.sharma@berrybloomer.com
            </EmailLink>
          </InfoItem>

          <InfoItem>
            <IconBox>
              <Phone size={16} />
            </IconBox>
            <PhoneLink href="tel:+917976160206">+91 7976160206</PhoneLink>
          </InfoItem>

          <InfoItemTop>
            <IconBox>
              <MapPin size={16} />
            </IconBox>
            <AddressText>
              <strong>Greater Noida Office</strong><br />
              Near ACE Divine, Ground Floor Aimnabad,<br />
              Greater Noida, UP – 203207
              <br /><br />
              <strong>Bharatpur Office</strong><br />
              iStart Nest Incubation Center,<br />
              Rajasthan – 321001
            </AddressText>
          </InfoItemTop>
        </Left>

        {/* RIGHT */}
        <Right>
          <FormTitle>Send Us a Message</FormTitle>
          <Form onSubmit={(e) => e.preventDefault()}>
            <Input placeholder="Your Name" />
            <Input placeholder="Email Address" type="email" />
            <Input placeholder="Phone Number" type="tel" />
            <Textarea placeholder="Your Message" />
            <Button type="submit">Send Message</Button>
          </Form>
        </Right>
      </Card>
    </PageWrapper>
  );
}
